<template>
<div class="map-editor-modal-wrapper" @click="$emit('close')">
  <div class="map-editor-modal-container row" @click.stop>
    <div id="map-editor"></div>
    <div class="address-input">
      <input-places class="col-md-6 col-md-offset-3"
        @place-selected="setPositionFromAddress"
        :attrs="{id: 'input-places-map-editor', placeholder: attrs.placeholder}"
        >
      </input-places>
    </div>
    <div v-if="showHelp" class="map-help col-md-3" transition="fade">
      <a class="close clickable" @click="showHelp = false">&times;</a>
      <p>{{ $t('map.help') }}</p>
    </div>
    <div>
      <button class="btn btn-primary" @click="$emit('close')">
        {{ $t('ok') }}
      </button>
    </div>
  </div>
</div>
</template>

<script>
// Map editor component
// Modal component that allows the user to define the marker fence on a map
// The user can pick a location (drag-and-drop or double-click) and a radius
// on the map directly, or type an address in the google places input

import L from 'leaflet'
import CircleEditor from '../../libs/maps/circleEditor.js'
import debounce from 'lodash.debounce'
import InputPlaces from './InputPlaces.vue'

// Map tiles service URL
// See leaflet documentation for a list of compatible map tile services
const mapboxUrl = 'https://api.mapbox.com/styles/v1/philippe-look/cisadedxf003i2zo3t5ruhi23/tiles/256/{z}/{x}/{y}?access_token=pk.eyJ1IjoicGhpbGlwcGUtbG9vayIsImEiOiJjaXNhZDl3aHQwMDJjMnVwNWtibDR2azBvIn0.rT49BllHbiANRj9yQL0ozQ'

function addMapboxLayerTo (mapHandle) {
  L.tileLayer(mapboxUrl).addTo(mapHandle)
}

var circleOptions = {
  stroke: true,
  color: '#ff2d55',
  weight: 1,
  dashArray: '6, 6',
  opacity: 1,
  fill: true,
  fillColor: '#ff2d55',
  fillOpacity: 0.05,
  clickable: false
}

export default {
  props: [
    'position', 'attrs', 'maxRadius'
  ],
  data: function () {
    return {
      showHelp: true
    }
  },
  components: {
    InputPlaces
  },
  mounted: function () {
    this.address = this.position.address
    this.setupMapEditor()
  },
  methods: {
    setupMapEditor: function () {
      var mapEditor = L.map('map-editor')
        .setView([this.position.lat, this.position.long], 13)
      addMapboxLayerTo(mapEditor)

      var circle = new CircleEditor([this.position.lat, this.position.long], this.position.radius, circleOptions)
      mapEditor.addLayer(circle)

      if (this.position.lat === 0 && this.position.long === 0) {
        mapEditor.fitWorld().zoomIn()
      } else {
        mapEditor.fitBounds(circle.getBounds(), {padding: [50, 50]})
      }

      mapEditor.doubleClickZoom.disable()
      circle.on('centerchange', debounce(this.centerChanged, 200))
      circle.on('radiuschange', debounce(this.radiusChanged, 200))
      mapEditor.on('dblclick', this.doubleClickHandler)
      mapEditor.on('viewreset', this.viewResetHandler)

      this.mapEditor = {}
      this.mapEditor.lmap = mapEditor
      this.mapEditor.circle = circle
    },
    centerChanged: function () {
      this.$emit('position-changed', this.getPosition())
    },
    radiusChanged: function () {
      var radius = this.maxRadiusFilter(this.mapEditor.circle.getRadius())
      this.mapEditor.circle.updateRadius(radius)
      this.$emit('position-changed', this.getPosition())
    },
    doubleClickHandler: function (mouseEvent) {
      this.address = ''
      this.moveCenter(mouseEvent.latlng.lat, mouseEvent.latlng.lng)
    },
    viewResetHandler: function () {
      setTimeout(() => {
        this.mapEditor.circle.detectOverlappingMakers()
        this.mapEditor.circle.orientGizmo()
      }, 100)
    },
    moveCenter: function (lat, long) {
      this.mapEditor.circle.setLatLng([lat, long])
      this.mapEditor.circle.resetMarkers()
      this.mapEditor.circle.fire('centerchange')
    },
    getPosition: function () {
      var radius = this.mapEditor.circle.getRadius()
      var latlng = this.mapEditor.circle.getLatLng().wrap()
      return {lat: latlng.lat, long: latlng.lng, radius: radius, address: this.address}
    },
    setPositionFromAddress: function (position) {
      this.address = position.address
      this.moveCenter(position.lat, position.long)
      this.mapEditor.lmap.fitBounds(this.mapEditor.circle.getBounds(), {padding: [50, 50]})
    },
    maxRadiusFilter: function (newRadius) {
      var radius = Math.round(newRadius)
      if (radius > this.maxRadius) {
        radius = this.maxRadius
      }
      return radius
    }
  }
}
</script>

<style>
/* overwrite leaflet styles */
.leaflet-control-layers-toggle {
  background-image: url(/static/img/leaflet/layers.png) !important;
}
.leaflet-retina .leaflet-control-layers-toggle {
  background-image: url(/static/img/leaflet/layers-2x.png) !important;
}
.marker-edit-center-icon {
  background-image: url(../../assets/img/LocationPin.png) !important;
}
.marker-edit-resize-icon {
  background-image: url(../../assets/img/ResizeArrows.png) !important;
}
</style>
